<script setup lang="ts">
import { authClient } from '~/lib/auth-client'
import { ROUTES } from '~/utils/constants'

const { data: session } = await authClient.useSession(useFetch)
const router = useRouter()

const { isCollapsed, toggle: toggleSidebar } = useSidebar()

const colorMode = useColorMode()
const toggleTheme = () => {
  colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'
}

const handleLogout = async () => {
  await authClient.signOut()
  router.push(ROUTES.HOME)
}

const links = [
  { label: 'Dashboard', icon: 'i-heroicons-home', to: ROUTES.DASHBOARD },
  {
    label: 'Mock Test',
    icon: 'i-heroicons-clipboard-document-list',
    to: ROUTES.EXAMS,
  },
]

const avatarItems = [
  [
    {
      label: 'Settings',
      icon: 'i-heroicons-cog-8-tooth',
      to: ROUTES.SETTINGS,
    },
  ],
  [
    {
      label: 'Logout',
      icon: 'i-heroicons-arrow-left-on-rectangle',
      onSelect: handleLogout,
    },
  ],
]

const copied = ref(false)
const copyFullEmail = () => {
  const email = session.value?.user?.email
  if (!email) return
  navigator.clipboard.writeText(email).then(() => {
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  })
}
const emailPrefix = computed(() => {
  const email = session.value?.user?.email
  if (!email) return ''
  return email.split('@')[0]
})
</script>

<template>
  <div
    id="layout-root"
    class="min-h-screen grid grid-cols-1 md:grid-cols-[auto_1fr] bg-gray-50 dark:bg-gray-950"
  >
    <!-- Sidebar -->
    <aside
      id="layout-sidebar"
      class="hidden md:flex flex-col border-r border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl h-screen sticky top-0 transition-all duration-300 ease-in-out z-40 overflow-hidden shadow-sm"
      :class="[isCollapsed ? 'w-16' : 'w-64']"
    >
      <!-- Sidebar Header -->
      <div
        class="flex shrink-0 transition-all duration-300 border-b border-gray-200 dark:border-gray-800"
        :class="[
          isCollapsed
            ? 'flex-col items-center gap-4 pt-6 pb-2 px-2'
            : 'h-16 flex-row items-center justify-between px-4',
        ]"
      >
        <div
          class="flex items-center gap-2 overflow-hidden transition-all duration-300 shrink-0"
          :class="{ 'order-2': isCollapsed }"
        >
          <Logo :to="ROUTES.DASHBOARD" size="md" :collapsed="isCollapsed" />
        </div>

        <UButton
          color="neutral"
          variant="ghost"
          size="md"
          @click="toggleSidebar"
          class="transition-all duration-300 z-50 flex items-center justify-center p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg shrink-0"
          :class="[isCollapsed ? 'order-1' : '']"
        >
          <img
            :src="isCollapsed ? '/images/right.svg' : '/images/left.svg'"
            class="w-5 h-5 dark:invert"
            alt="Toggle Sidebar"
          />
        </UButton>
      </div>

      <!-- Sidebar Navigation -->
      <div class="grow p-2 space-y-1 overflow-y-auto overflow-x-hidden pt-2">
        <template v-for="link in links" :key="link.to">
          <UTooltip :text="link.label" :side="'right'" :disabled="!isCollapsed">
            <NuxtLink
              :to="link.to"
              class="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative"
              :class="[
                router.currentRoute.value.path.startsWith(link.to)
                  ? 'bg-primary-50 text-primary-600 dark:bg-primary-950/30 dark:text-primary-400 font-semibold shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white',
                isCollapsed ? 'justify-center px-0' : 'justify-start',
              ]"
            >
              <UIcon :name="link.icon" class="w-6 h-6 shrink-0" />
              <span
                v-if="!isCollapsed"
                class="text-sm tracking-tight whitespace-nowrap overflow-hidden transition-all duration-300"
              >
                {{ link.label }}
              </span>
              <!-- Active Indicator (Vertical line) -->
              <div
                v-if="router.currentRoute.value.path.startsWith(link.to)"
                class="absolute left-0 top-2 bottom-2 w-1 bg-primary-500 rounded-r-full"
              ></div>
            </NuxtLink>
          </UTooltip>
        </template>
      </div>

      <!-- Sidebar Footer / User Profile -->
      <div
        id="layout-sidebar-bottom"
        class="p-2 border-t border-gray-200 dark:border-gray-800 shrink-0 space-y-2"
      >
        <!-- Theme Changer Toggle -->
        <UButton
          id="theme-toggle-btn"
          @click="toggleTheme"
          color="neutral"
          variant="ghost"
          class="w-full flex items-center rounded-xl text-sm transition-colors cursor-pointer"
          :class="[isCollapsed ? 'justify-center p-2' : 'px-3 py-2 justify-start']"
        >
          <div :class="['flex items-center', isCollapsed ? '' : 'gap-3']">
            <UIcon
              v-if="colorMode.value === 'light'"
              name="i-heroicons-sun"
              class="w-5 h-5 text-amber-500 shrink-0"
            />
            <UIcon v-else name="i-heroicons-moon" class="w-5 h-5 text-indigo-400 shrink-0" />
            <span
              v-if="!isCollapsed"
              class="text-sm font-medium tracking-tight text-gray-500 dark:text-gray-400"
              >{{ colorMode.value === 'light' ? 'Light Theme' : 'Theme Shifted' }}</span
            >
          </div>
        </UButton>

        <UDropdownMenu
          :items="avatarItems"
          :content="{ align: 'start', side: 'right', sideOffset: 8 }"
        >
          <div
            class="flex items-center gap-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer transition-colors w-full overflow-hidden"
            :class="{ 'justify-center px-0': isCollapsed }"
          >
            <UAvatar
              :src="session?.user?.image || '/images/flag.png'"
              :alt="session?.user?.name"
              size="sm"
              class="shrink-0"
            />
            <div class="grow min-w-0 text-left transition-all duration-300" v-if="!isCollapsed">
              <p class="text-sm font-medium truncate">{{ session?.user?.name || 'User' }}</p>
              <div class="mt-0.5 flex">
                <span
                  @click.stop="copyFullEmail"
                  class="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold bg-primary-50/80 hover:bg-primary-100 dark:bg-primary-950/40 dark:hover:bg-primary-900/40 text-primary-600 dark:text-primary-400 cursor-pointer select-none border border-primary-200/30 dark:border-primary-800/40 max-w-[150px] truncate transition-all duration-200"
                  :title="session?.user?.email || 'Click to copy email'"
                >
                  <span class="truncate">{{ emailPrefix }}</span>
                  <UIcon
                    v-if="copied"
                    name="i-heroicons-check"
                    class="w-3 h-3 text-emerald-500 shrink-0 ml-1"
                  />
                </span>
              </div>
            </div>
            <UIcon
              v-if="!isCollapsed"
              name="i-heroicons-ellipsis-vertical"
              class="w-4 h-4 text-gray-400 shrink-0"
            />
          </div>
        </UDropdownMenu>
      </div>
    </aside>

    <!-- Content Area -->
    <div id="layout-content" class="flex flex-col min-h-screen min-w-0 overflow-y-auto">
      <header
        class="md:hidden h-16 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between px-4 sticky top-0 z-40"
      >
        <Logo :to="ROUTES.DASHBOARD" size="sm" />
        <UButton icon="i-heroicons-bars-3" :color="'gray' as any" variant="ghost" />
      </header>

      <main id="layout-main" class="grow w-full">
        <slot />
      </main>
      <UIBackToTop />
    </div>
  </div>
</template>
